import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabDescriptor, OperationBC } from '../types';
import { buildOperationTabs, computeDefaultTabIndex } from '../domain/operationTabs';
import { resolveTabParam } from '../../../utils/tabPreference';

// Hook mince : la logique (visibilite/gating/onglet par defaut) vit dans
// domain/operationTabs. Ici, uniquement le cablage de l'onglet actif a l'URL (?tab=).
export const useOperationTabs = (operation: OperationBC | null) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const descriptors = useMemo<TabDescriptor[]>(
    () => (operation ? buildOperationTabs(operation) : []),
    [operation],
  );

  const defaultIndex = useMemo(
    () => (operation ? computeDefaultTabIndex(descriptors, operation) : 0),
    [descriptors, operation],
  );

  const tabParam = searchParams.get('tab');
  // ?tab= porte une LISTE de preferences resolues par index, cle ou kind (cf. utils/tabPreference).
  // La cle est indispensable ici : `offre-2` est le seul moyen de viser un rang precis, les onglets
  // Offre partageant tous le kind `offre`. Un onglet grise n'est jamais retenu.
  const activeIndex = useMemo(() => {
    const resolved = resolveTabParam(tabParam, descriptors);
    return resolved >= 0 ? resolved : defaultIndex;
  }, [tabParam, defaultIndex, descriptors]);

  // L'onglet actif est persiste dans l'URL : compatible avec les feuilles qui
  // rechargent la page, et avec la navigation depuis les listes.
  const setActiveIndex = useCallback(
    (index: number) => {
      const target = descriptors[index];
      if (!target || target.disabled || target.inactive) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', String(index));
      setSearchParams(next, { replace: true });
    },
    [descriptors, searchParams, setSearchParams],
  );

  return {
    descriptors,
    activeIndex,
    activeTab: descriptors[activeIndex] ?? null,
    setActiveIndex,
  };
};
